import { useEffect, type ReactNode } from 'react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { UMRail, UM_RAIL_ITEMS, type UMRailItem, type UMRailItemId } from './UMRail';
import { UMR } from './referenceTokens';

/** PrototypeStudioLayout header height (measured: 48px). */
const STUDIO_HEADER = 48;

export interface UMAppShellProps {
  /** Title shown in the Studio header (breadcrumb). */
  title: string;
  railItems?: UMRailItem[];
  activeRailId?: UMRailItemId | null;
  onRailSelect?: (id: UMRailItemId) => void;
  /** Top-left toolbar overlay, normally <UMMapToolbar />. */
  toolbar?: ReactNode;
  /** Panel docked right of the rail, under the toolbar (production: mt-49px, 300px). */
  leftPanel?: ReactNode;
  /** Panel overlaid on the right edge of the map (object detail, e.g. UMBuildingDetailPanel). Not in flow, like production's fluid-modal-side. */
  rightPanel?: ReactNode;
  /** Map surface, normally <UMMap /> + <UMMapControls />. Fills the remaining space. */
  children: ReactNode;
  /** Set false to render without the Studio header (e.g. embedded component previews). */
  withStudioLayout?: boolean;
}

/**
 * Faithful UrbanMap home chrome: 80px rail + full-bleed map + toolbar overlay
 * + optional left/right docked panels. Measured on urbanmap.myengie.com.
 * See docs/urbanmap/reference/home-map.md.
 */
export function UMAppShell({
  title,
  railItems = UM_RAIL_ITEMS,
  activeRailId = null,
  onRailSelect,
  toolbar,
  leftPanel,
  rightPanel,
  children,
  withStudioLayout = true,
}: UMAppShellProps) {
  useEffect(() => {
    if (document.querySelector(`link[href="${UMR.fontHref}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = UMR.fontHref;
    document.head.appendChild(link);
  }, []);
  const shell = (
    <div
      data-um-shell=""
      style={{
        display: 'flex',
        height: withStudioLayout ? `calc(100vh - ${STUDIO_HEADER}px)` : '100%',
        width: '100%',
        overflow: 'hidden',
        background: UMR.white,
        fontFamily: UMR.font,
        color: UMR.black,
        position: 'relative',
      }}
    >
      <UMRail items={railItems} activeId={activeRailId} onSelect={onRailSelect} />
      <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex' }}>
        {toolbar && (
          <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 20, pointerEvents: 'none', display: 'flex' }}>
            {toolbar}
          </div>
        )}
        {leftPanel && (
          <div style={{ marginTop: UMR.size.toolbarHeight, height: `calc(100% - ${UMR.size.toolbarHeight}px)`, zIndex: 15 }}>
            {leftPanel}
          </div>
        )}
        <main style={{ position: 'relative', flex: 1, minWidth: 0 }}>{children}</main>
        {rightPanel && <div style={{ position: 'absolute', top: 0, right: 0, height: '100%', zIndex: 25 }}>{rightPanel}</div>}
      </div>
    </div>
  );
  return withStudioLayout ? <PrototypeStudioLayout title={title}>{shell}</PrototypeStudioLayout> : shell;
}
